import { BASE } from './client'
import type {
  AccessEvent,
  AccessGroup,
  AccessSystem,
  CommandLog,
  Credential,
  Door,
  DoorCode,
  DoorCodeSeries,
  DoorPolicy,
  IntercomContact,
  LocationNode,
  MemberRule,
  Occupancy,
  Operator,
  Person,
  Schedule,
  SyncRun,
  UnknownCredential,
  WebhookSubscription,
  Zone,
} from './types'

// --- Server → client messages (see the /ws contract) ---

export interface HelloMessage {
  type: 'hello'
  customer: string
  role: string
  at: string
}

export interface PingMessage {
  type: 'ping'
  at: string
}

/** Reply to a client `{"type":"auth","token":...}` frame: fresh token accepted. */
export interface AuthOkMessage {
  type: 'auth_ok'
}

/** Reply to a client `auth` frame: customer mismatch — the server then closes with 4403. */
export interface AuthErrorMessage {
  type: 'auth_error'
}

export type ChangeAction = 'created' | 'updated' | 'deleted'

export type ChangeMessage =
  | { type: 'change'; kind: 'event'; action: ChangeAction; object: AccessEvent; at: string }
  | { type: 'change'; kind: 'command'; action: ChangeAction; object: CommandLog; at: string }
  | { type: 'change'; kind: 'system'; action: ChangeAction; object: AccessSystem; at: string }
  | { type: 'change'; kind: 'sync_run'; action: ChangeAction; object: SyncRun; at: string }
  | { type: 'change'; kind: 'unknown_credential'; action: ChangeAction; object: UnknownCredential; at: string }
  | { type: 'change'; kind: 'person'; action: ChangeAction; object: Person; at: string }
  | { type: 'change'; kind: 'occupancy'; action: ChangeAction; object: Occupancy; at: string }
  | { type: 'change'; kind: 'location'; action: ChangeAction; object: LocationNode; at: string }
  | { type: 'change'; kind: 'door'; action: ChangeAction; object: Door; at: string }
  | { type: 'change'; kind: 'zone'; action: ChangeAction; object: Zone; at: string }
  | { type: 'change'; kind: 'credential'; action: ChangeAction; object: Credential; at: string }
  | { type: 'change'; kind: 'access_group'; action: ChangeAction; object: AccessGroup; at: string }
  | { type: 'change'; kind: 'schedule'; action: ChangeAction; object: Schedule; at: string }
  | { type: 'change'; kind: 'member_rule'; action: ChangeAction; object: MemberRule; at: string }
  | { type: 'change'; kind: 'door_policy'; action: ChangeAction; object: DoorPolicy; at: string }
  | { type: 'change'; kind: 'door_code'; action: ChangeAction; object: DoorCode; at: string }
  | { type: 'change'; kind: 'door_code_series'; action: ChangeAction; object: DoorCodeSeries; at: string }
  | { type: 'change'; kind: 'intercom_contact'; action: ChangeAction; object: IntercomContact; at: string }
  | { type: 'change'; kind: 'webhook'; action: ChangeAction; object: WebhookSubscription; at: string }
  | { type: 'change'; kind: 'operator'; action: ChangeAction; object: Operator; at: string }

export type ServerMessage = HelloMessage | PingMessage | AuthOkMessage | AuthErrorMessage | ChangeMessage

export type LiveStatus = 'connected' | 'connecting' | 'disconnected'

/** Close code the server uses for an invalid/expired token. */
export const CLOSE_AUTH_EXPIRED = 4401
/** Close code the server uses when a re-auth token is for a different customer. */
export const CLOSE_AUTH_MISMATCH = 4403

const RECONNECT_MIN_MS = 1_000
const RECONNECT_MAX_MS = 30_000

/** Build the /ws URL from the same BASE the REST client uses, http→ws. */
export function consoleWsUrl(token: string): string {
  if (BASE.startsWith('http')) {
    const url = new URL(BASE)
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
    url.pathname = `${url.pathname.replace(/\/+$/, '')}/ws`
    url.search = new URLSearchParams({ token }).toString()
    return url.toString()
  }
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
  return `${proto}://${window.location.host}${BASE}/ws?token=${encodeURIComponent(token)}`
}

interface ConsoleSocketHandlers {
  onMessage?: (msg: ServerMessage) => void
  onStatus?: (status: LiveStatus) => void
  /**
   * The server closed the socket for an auth reason: CLOSE_AUTH_EXPIRED
   * (invalid/expired token — caller may renew and reconnect) or
   * CLOSE_AUTH_MISMATCH (re-auth token for another customer — fatal).
   */
  onAuthFailure?: (code: number) => void
  /**
   * Gate consulted before every automatic reconnect attempt. Resolve false
   * to stop reconnecting (e.g. the token cannot be renewed).
   */
  onBeforeConnect?: () => Promise<boolean>
}

/**
 * Typed /ws client with auto-reconnect (exponential backoff, 1s → 30s cap).
 * The socket counts as `connected` — and the backoff resets — only once the
 * server's `hello` arrives, not on the transport `open`: the server accepts
 * the upgrade before it has confirmed its Valkey subscription, and closes
 * with 1011 right after when that fails. Treating
 * `open` as success would flash "live" and reset the backoff on every such
 * attempt, hammering a degraded server at the 1s floor.
 *
 * The URL is resolved through the provider at every attempt so a renewed
 * token is picked up automatically. No-ops after close() until connect() is
 * called again.
 */
export class ConsoleSocket {
  private ws: WebSocket | null = null
  private attempts = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private stopped = true
  private urlFor: () => string = () => ''

  constructor(private readonly handlers: ConsoleSocketHandlers) {}

  /** Connect; `urlFor` is re-evaluated on every (re)connect attempt. */
  connect(urlFor: () => string): void {
    this.urlFor = urlFor
    this.stopped = false
    this.attempts = 0
    this.open()
  }

  close(): void {
    this.stopped = true
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    const ws = this.ws
    this.ws = null
    if (ws && ws.readyState !== WebSocket.CLOSED && ws.readyState !== WebSocket.CLOSING) {
      ws.close()
    }
    this.handlers.onStatus?.('disconnected')
  }

  /** True after close() or an auth close, until connect() is called again. */
  isStopped(): boolean {
    return this.stopped
  }

  /** Send an in-band re-auth frame with a fresh token; no-op unless the socket is open. */
  sendAuth(token: string): void {
    const ws = this.ws
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'auth', token }))
    }
  }

  private open(): void {
    this.handlers.onStatus?.('connecting')
    const ws = new WebSocket(this.urlFor())
    this.ws = ws

    ws.onmessage = (ev: MessageEvent<string>) => {
      if (this.ws !== ws) return
      let msg: ServerMessage
      try {
        msg = JSON.parse(ev.data) as ServerMessage
      } catch {
        return
      }
      if (msg.type === 'hello') {
        // The server's handshake is complete: only now is the connection live.
        this.attempts = 0
        this.handlers.onStatus?.('connected')
      }
      this.handlers.onMessage?.(msg)
    }

    ws.onclose = (ev) => {
      if (this.ws !== ws) return
      this.ws = null
      if (ev.code === CLOSE_AUTH_EXPIRED || ev.code === CLOSE_AUTH_MISMATCH) {
        this.stopped = true
        this.handlers.onStatus?.('disconnected')
        this.handlers.onAuthFailure?.(ev.code)
        return
      }
      if (this.stopped) {
        this.handlers.onStatus?.('disconnected')
        return
      }
      this.handlers.onStatus?.('connecting')
      const delay = Math.min(RECONNECT_MIN_MS * 2 ** this.attempts, RECONNECT_MAX_MS)
      this.attempts += 1
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null
        if (!this.stopped) void this.retry()
      }, delay)
    }
  }

  /** Reconnect after the backoff delay, unless the gate vetoes it. */
  private async retry(): Promise<void> {
    const proceed = (await this.handlers.onBeforeConnect?.()) ?? true
    if (this.stopped) return
    if (!proceed) {
      this.stopped = true
      this.handlers.onStatus?.('disconnected')
      return
    }
    this.open()
  }
}
